import { RelatedDeltaClient } from '../../Applicant.types';
import { ApplicantKeyValuePair, ApplicantKeyValueTable } from '../ApplicantKeyValuePair';
import {
    ApplicantItemWrapper,
    ExternalLinkIcon,
    MatchedOnList,
} from './ApplicantRelatedAccounts.StyledComponents';
import { RelatedAccountsCard } from './RelatedAccountsCard';

interface SectionProps extends React.ComponentPropsWithRef<'div'> {
    accountsType: string;
    accounts: RelatedDeltaClient[];
    isLoading?: boolean;
    isLoadingError?: boolean;
    onRetry?: () => void;
}

export const DeltaClientsSection = ({
    accountsType,
    accounts,
    isLoading = false,
    isLoadingError = false,
    onRetry,
    ...props
}: SectionProps) => {
    const relatedAccountKey = `${accountsType}-related-account`;

    const renderClient = (client: RelatedDeltaClient, i: number) => {
        const itemKey = `${relatedAccountKey}-${i}`;

        return (
            <ApplicantItemWrapper key={itemKey} data-testid={itemKey}>
                <ApplicantKeyValueTable>
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-account-type`}
                        label="Related Account Type"
                        value={
                            client.isDirectlyLinked
                                ? 'Created from this Applicant'
                                : 'Suspected Relationship — Could be same Carrier?'
                        }
                    />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-delta-client-id`}
                        label="Delta Client ID"
                        value={
                            client.deltaClientLink ? (
                                <a href={client.deltaClientLink} target="_blank" rel="noreferrer">
                                    {client.deltaClientId} <ExternalLinkIcon>{'\u{238B}'}</ExternalLinkIcon>
                                </a>
                            ) : (
                                client.deltaClientId
                            )
                        }
                    />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-carrier-name`}
                        label="Carrier Name"
                        value={client.carrierName}
                    />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-status`}
                        label="Status in Delta"
                        value={client.isInactive ? 'Inactive' : 'Active'}
                    />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-has-funded`}
                        label="Has Funded"
                        value={client.lastFundingDate ? `Yes (Last Funded: ${client.lastFundingDate})` : 'No'}
                    />
                    <ApplicantKeyValuePair dataTestId={`${itemKey}-us-dot`} label="US DOT" value={client.usDot} />
                    <ApplicantKeyValuePair dataTestId={`${itemKey}-mc`} label="MC#" value={client.mc} />
                    <ApplicantKeyValuePair dataTestId={`${itemKey}-email`} label="Email" value={client.email} />
                    <ApplicantKeyValuePair dataTestId={`${itemKey}-phone`} label="Phone" value={client.phone} />
                    <ApplicantKeyValuePair
                        dataTestId={`${itemKey}-matched-on`}
                        label="Matched On"
                        value={
                            <MatchedOnList>
                                {client.matchedOn.map((match) => (
                                    <li key={match}>{match}</li>
                                ))}
                            </MatchedOnList>
                        }
                    />
                    {client.originationsApplicantId ? (
                        <ApplicantKeyValuePair
                            dataTestId={`${itemKey}-originations-link`}
                            label="Originations Applicant"
                            value={
                                <a
                                    href={`/applicant/${client.originationsApplicantId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    View Applicant <ExternalLinkIcon>{'\u{238B}'}</ExternalLinkIcon>
                                </a>
                            }
                        />
                    ) : null}
                </ApplicantKeyValueTable>
            </ApplicantItemWrapper>
        );
    };

    return (
        <RelatedAccountsCard
            {...props}
            data-testid={relatedAccountKey}
            title={`${accountsType} Accounts`}
            count={accounts.length}
            isLoading={isLoading}
            isLoadingError={isLoadingError}
            onRetry={onRetry}
            emptyMessage={`No related ${accountsType} accounts found.`}
            errorMessage={`Could not load ${accountsType} accounts.`}
        >
            {accounts.map(renderClient)}
        </RelatedAccountsCard>
    );
};
